import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  AfterViewInit,
  HostListener,
  HostBinding,
  ElementRef,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { CAndCThemePalette } from '@design-system/theme/candc-theme-palette';
import { IconColor, IconConfig, IconSize } from '@design-system/buttons/icon/icon-config';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { IconComponent } from '../icon/icon.component';
import { CommonModule } from '@angular/common';
import { BadgeComponent } from '@design-system/layout/badge/badge.component';

export type ButtonType = 'ng-content-button' | 'icon-button' | 'text-icon-button' | 'tab-button' | 'basic-button';
export type ButtonWidth = 'medium';

/** Physical button dimensions / padding. Use with {@link IconSize} when you need a smaller control (e.g. circle + icon). */
export type ButtonSize = 'default' | 'small';

@Component({
  selector: 'app-button',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    IconComponent,
    BadgeComponent
  ]
})
export class ButtonComponent extends MatButton implements AfterViewInit, OnChanges {
  private _loading = false;
  private __disabled: any = false;
  private __tooltip = '';
  @Input() type = 'button';
  private _focussed = false;
  @Input() set focussed(value: boolean) {
    this._focussed = value;
    if (value && this.buttonWrapper) {
      this.focusButton();
    }
  }
  get focussed(): boolean {
    return this._focussed;
  }
  @Input() theme: CAndCThemePalette = 'primary';
  @Input() useSymbol?: boolean;
  @Input() icon?: string;
  @Input() iconRight = false; //Icon left aligned by default
  @Input() iconSize?: IconSize = 'small';
  @Input() iconColor?: IconColor = 'white';
  @Input() iconHoverColor?: IconColor = 'white';
  @Input() iconOutlined?: boolean;
  @Input() uppercaseText = true;
  @Input() badgeCount?: number;
  @Input() systemDisabled = false;
  @Input() tooltipWhenDisabled?: string;
  @Input() hoverUnderline = false;
  @Input() fontColor?: CAndCThemePalette;
  @Input() tabButton?: boolean;
  @Input() iconConfig?: IconConfig;
  @Input() circle = false;
  @Input() fullWidth = false;
  /** Smaller padding and dimensions; circle buttons use a reduced diameter. */
  @Input() buttonSize: ButtonSize = 'default';

  @HostBinding('class.button-size-small')
  get hostButtonSizeSmall(): boolean {
    return this.buttonSize === 'small';
  }

  get loadingSpinnerDiameter(): number {
    return this.buttonSize === 'small' ? 16 : 20;
  }

  get loadingSpinnerStrokeWidth(): number {
    return this.buttonSize === 'small' ? 2 : 3;
  }

  @Input() override set disabled(disabled: boolean) {
    this.__disabled = disabled;
  }
  override get disabled(): boolean {
    return this.__disabled || this._loading || this.systemDisabled;
  }

  @Input() set loading(loading: boolean | undefined | null) {
    this._loading = loading ?? false;
  }
  get loading(): boolean {
    return this._loading;
  }

  @Input() set tooltip(tooltip: string | undefined) {
    this.__tooltip = tooltip ?? '';
  }

  get tooltip(): string {
    return this.disabled && this.tooltipWhenDisabled ? this.tooltipWhenDisabled : this.__tooltip;
  }

  @ViewChild('buttonWrapper') buttonWrapper!: ElementRef<HTMLElement>;

  override ngAfterViewInit(): void {
    if (this.focussed) {
      this.focusButton();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['focussed'] && changes['focussed'].currentValue && this.buttonWrapper) {
      // Use setTimeout to ensure the view is updated
      setTimeout(() => this.focusButton(), 0);
    }
  }

  private focusButton(): void {
    if (!this.buttonWrapper) return;
    const buttonElement = this.buttonWrapper.nativeElement.querySelector('button');
    if (buttonElement) {
      buttonElement.focus();
    }
    this._focussed = false;
  }

   
  @Output() clickEvent = new EventEmitter<void>();

  get ngClass(): string[] {

    const classes: string[] = [this.buttonType, (this.iconRight === false && !!this.icon) ? 'icon-left' : 'icon-right'];

    if(this.theme === 'primary-action' || this.theme === 'primary') {
      classes.push('primary-action');
      this.iconColor = 'white';
      this.iconHoverColor = 'white';
    }

    if(this.theme === 'secondary-action' || this.theme === 'underline') {
      this.iconColor = 'primary-action';
      this.iconHoverColor = 'primary-action';
    }

    if (this.theme === 'alternate') {
      this.iconColor = 'ink-100';
      this.iconHoverColor = 'ink-100';
    }

    if (this.uppercaseText) classes.push('uppercase-text');

    if (this.hoverUnderline) classes.push('underline-text-on-hover');

    if(this.circle) classes.push('circle');

    if(this.icon) classes.push('icon-button');

    if (this.__disabled === true || this.systemDisabled === true) classes.push('button-disabled');

    if (this.fontColor !== undefined) classes.push('text-color-' + this.fontColor);

    if (this.fullWidth) classes.push('full-width');

    if (this.buttonSize === 'small') classes.push('button-size-small');

    return classes;
  }

  get buttonType(): ButtonType {  
    if (this.tabButton !== undefined) return 'tab-button';
    if (this.icon !== undefined) return 'text-icon-button';
    return 'ng-content-button';
  }

  onWrapperClick(event$: any): void {
    if (this.disabled) {
      event$.stopPropagation();
      event$.preventDefault();
    } else {
      this.clickEvent.emit(event$);
    }
  }

  // Override the click output to prevent firing when disabled
  @HostListener('click', ['$event'])
  onHostClick(event: Event): void {
    if (this.disabled) {
      event.stopPropagation();
      event.preventDefault();
    }
  }
}
